import { inject, Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { ODataList } from '../entities/academy-entities.model';
import { ODataQuery } from '../entities/academy-query.model';
import {
  ChapterCreateRequest,
  ChapterResponse,
  TranscriptCueCreateRequest,
  TranscriptCueResponse,
} from '../entities/academy.model';
import { AcademyCrudService } from './academy-crud.service';
import { AcademyODataService } from './academy-odata.service';

/**
 * Producer-side authoring of recording **chapters** and **transcript cues** over the
 * generic OData sets `Chapters` / `TranscriptCues` (integration guide §7.10 — "CRUD/OData
 * base, lado produtor"). The gated consumer reads live in `ContentReadService`.
 * Bulk cue import is repeated `POST`s (the guide's documented mass-import path).
 */
@Injectable({ providedIn: 'root' })
export class ContentAuthoringService {
  private readonly odata = inject(AcademyODataService);
  private readonly crud = inject(AcademyCrudService);

  /** `POST /Chapters` — add a chapter marker to a recording (`ChapterId` is server-assigned). */
  public createChapter(
    viewModel: ChapterCreateRequest
  ): Observable<ChapterResponse> {
    return this.crud.create<ChapterResponse>('Chapters', viewModel);
  }

  /**
   * `GET /odata/Chapters` — list chapters, filtered by `VideoAssetId` (the only owner column
   * on the entity). Recordings can't be filtered by `LiveEventId` here — it isn't persisted —
   * so live authoring reads via the custom `/LiveEvents/{id}/chapters` endpoint instead; this
   * OData path serves the course-upload (`VideoAssetId`) mode.
   */
  public listChapters(
    query: ODataQuery = {}
  ): Observable<ODataList<ChapterResponse>> {
    return this.odata.list<ChapterResponse>('Chapters', query);
  }

  /**
   * Bulk-create chapters as repeated `POST /Chapters` — the same documented mass-import
   * path as cues (the set exposes no batch endpoint). Resolves to the created chapters
   * in input order; an empty input resolves to `[]` without issuing any request.
   */
  public createChapters(
    viewModels: ChapterCreateRequest[]
  ): Observable<ChapterResponse[]> {
    if (viewModels.length === 0) return of([]);
    return forkJoin(viewModels.map((vm) => this.createChapter(vm)));
  }

  /** `POST /TranscriptCues` — add a single transcript cue (`TranscriptCueId` is server-assigned). */
  public createTranscriptCue(
    viewModel: TranscriptCueCreateRequest
  ): Observable<TranscriptCueResponse> {
    return this.crud.create<TranscriptCueResponse>('TranscriptCues', viewModel);
  }

  /**
   * `GET /odata/TranscriptCues` — list cues, filtered by `VideoAssetId` (and optionally
   * `LanguageCode`). As with chapters, `LiveEventId` isn't a column here; live authoring reads
   * via the custom `/LiveEvents/{id}/transcript` endpoint, leaving this OData path for the
   * course-upload (`VideoAssetId`) mode.
   */
  public listTranscriptCues(
    query: ODataQuery = {}
  ): Observable<ODataList<TranscriptCueResponse>> {
    return this.odata.list<TranscriptCueResponse>('TranscriptCues', query);
  }

  /**
   * Bulk-import cues as repeated `POST /TranscriptCues` (the guide's documented path for
   * mass import by language). Resolves to the created cues in input order; an empty input
   * resolves to `[]` without issuing any request.
   */
  public importTranscriptCues(
    viewModels: TranscriptCueCreateRequest[]
  ): Observable<TranscriptCueResponse[]> {
    if (viewModels.length === 0) return of([]);
    return forkJoin(viewModels.map((vm) => this.createTranscriptCue(vm)));
  }

  /** `DELETE /Chapters?id={chapterId}` — remove a chapter marker. */
  public deleteChapter(chapterId: string): Observable<void> {
    return this.crud.delete('Chapters', chapterId);
  }

  /** Bulk-remove chapters (repeated DELETEs); empty input resolves to `[]` without a request. */
  public deleteChapters(ids: string[]): Observable<void[]> {
    if (ids.length === 0) return of([]);
    return forkJoin(ids.map((id) => this.deleteChapter(id)));
  }

  /** `DELETE /TranscriptCues?id={cueId}` — remove a transcript cue. */
  public deleteTranscriptCue(cueId: string): Observable<void> {
    return this.crud.delete('TranscriptCues', cueId);
  }

  /** Bulk-remove cues (repeated DELETEs); empty input resolves to `[]` without a request. */
  public deleteTranscriptCues(ids: string[]): Observable<void[]> {
    if (ids.length === 0) return of([]);
    return forkJoin(ids.map((id) => this.deleteTranscriptCue(id)));
  }
}
